/** Datenzugriff über TanStack Query -- Cache, Wiederholung und Invalidierung. */

import { useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { nachrichtRumpf } from '../api/nachrichtRumpf'
import { ApiError, api } from '../api/client'
import { vergissOrt } from '../lib/letzterChat'
import { vorratLeeren } from '../lib/vorrat'
import { aufnahmeKopf, vorschauAus } from '../lib/images'
import { vorschauHochladen } from '../lib/vorschau'
import type { Login, Registration } from '../lib/passkey'
import type { Aufnahme } from '../lib/exif'
import type {
  Candidate,
  EnvSeite,
  Verwaltungsprotokoll,
  Selbstpruefung,
  Health,
  Highlight,
  HighlightsResponse,
  Invitation,
  Loeschbefund,
  Loeschvorschau,
  LocateResponse,
  LookupResponse,
  MapStyle,
  PublishManyResponse,
  SnapResponse,
  InvitationPreview,
  InvitationStatus,
  JoinRequest,
  Me,
  Dateiabgleich,
  Media,
  Medienpruefung,
  Member,
  Message,
  Person,
  MessagePage,
  Poll,
  PollsResponse,
  Reaction,
  Reisefotos,
  RescanResponse,
  Settings,
  SettingsResponse,
  Speicher,
  Thread,
  Track,
  TracksResponse,
  Trip,
  TripDeleteBefund,
  TripDeleteVorschau,
  TripResetBefund,
  TripResetVorschau,
  TripState,
  UmgebungResponse,
  Passkey,
  DayCardPreview,
} from '../api/types'

export const keys = {
  me: ['me'] as const,
  threads: ['threads'] as const,
  people: ['people'] as const,
  trips: ['trips'] as const,
  trip: (id: number) => ['trip', id] as const,
  members: (id: number) => ['members', id] as const,
  invitations: (id: number) => ['invitations', id] as const,
  requests: (id: number) => ['requests', id] as const,
  candidates: (id: number) => ['candidates', id] as const,
  invitationStatus: (code: string) => ['invitation-status', code] as const,
  settings: ['settings'] as const,
  passkeys: ['passkeys'] as const,
  messages: (tripId: number, withUser?: number) =>
    ['messages', tripId, withUser ?? null] as const,
  tracks: (tripId: number) => ['tracks', tripId] as const,
  highlights: (tripId: number) => ['highlights', tripId] as const,
  polls: (tripId: number) => ['polls', tripId] as const,
  reisefotos: (tripId: number) => ['reisefotos', tripId] as const,
  speicher: ['speicher'] as const,
  abgleich: ['abgleich'] as const,
}

/**
 * Wer hier ist -- und was er darf.
 *
 * **`refetchOnMount: 'always'` seit dem Vorrat (#13), und das ist kein
 * Feinschliff.** Seit der Zwischenspeicher das Schließen überlebt, kommt diese
 * Antwort beim Start aus der IndexedDB -- mit ihrem alten Alter. Innerhalb der
 * fünf Minuten Haltbarkeit hielt react-query sie damit für frisch und fragte
 * gar nicht nach. Im Prüflauf war das sofort zu sehen: ein gerade
 * freigeschaltetes Konto bekam weiter „Noch nichts freigegeben" zu lesen, und
 * ein Neuladen half nicht. Vorher konnte das nicht passieren, weil ein
 * Neuladen den Zwischenspeicher ohnehin leerte.
 *
 * Der gemerkte Stand wird trotzdem **sofort** gezeigt -- deshalb steht hier
 * `'always'` und nicht ein kürzeres `staleTime`: kein Ladebalken, keine
 * Weiterleitung zur Anmeldung, und die frische Auskunft kommt gleich
 * hinterher. Ohne Netz scheitert sie, und der gemerkte Stand bleibt stehen.
 * Genau so soll es sein.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/api/auth/me'),
    // Eine fehlende Anmeldung ist kein Fehler, der sich durch Wiederholen
    // beheben ließe.
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
  })
}

/**
 * Der Zustand der Installation -- nur gefragt, wenn etwas schiefgeht.
 *
 * Ausgeliefert wird per FTP, und das Schema zieht ein Mensch von Hand nach.
 * Bleibt dabei etwas liegen, ist die eigentliche Auskunft „es fehlt die
 * Spalte X" -- und die gehört vor die Nase dessen, der davorsteht, nicht in
 * ein Serverprotokoll.
 */
export function useHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Health>('/api/health'),
    enabled,
    retry: false,
  })
}

/**
 * Die einstellbaren Zahlen.
 *
 * Braucht jeder, nicht nur die Verwaltung: die Bildgröße wirkt im Browser,
 * und der verkleinert, bevor irgendetwas zum Server geht. Deshalb lange
 * haltbar und mit Vorgaben unterlegt -- ein Foto darf nicht darauf warten
 * müssen, dass diese Abfrage durch ist.
 */
export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<SettingsResponse>('/api/settings'),
    staleTime: 10 * 60_000,
  })
}

/**
 * Die Werte -- und zwar wirklich, nicht "vielleicht schon da".
 *
 * Für das Verkleinern eines Fotos reicht `useSettings()` nicht: wer ein Bild
 * auswählt, während die Abfrage noch unterwegs ist, bekäme die Vorgabe statt
 * der eingestellten Größe. Genau das ist im Test passiert -- und es wäre der
 * unangenehme Fall, weil das Foto dann *aussieht* wie eines, das der
 * Einstellung folgt.
 *
 * Läuft die Abfrage schon oder ist sie frisch, kostet das nichts.
 */
export async function ensureSettings(client: QueryClient): Promise<Settings | null> {
  try {
    const antwort = await client.ensureQueryData({
      queryKey: keys.settings,
      queryFn: () => api.get<SettingsResponse>('/api/settings'),
      staleTime: 10 * 60_000,
    })
    return antwort.settings
  } catch {
    // Ohne Antwort gelten die Vorgaben -- ein Foto soll nicht daran
    // scheitern, dass eine Nebensache nicht durchkam.
    return null
  }
}

export function useSaveSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (werte: Partial<Settings>) =>
      api.patch<SettingsResponse>('/api/settings', werte),
    onSuccess: (antwort) => {
      // Die Antwort trägt den geltenden Stand -- also gekappt, falls jemand
      // über die Decke der `.env` hinaus wollte. Sie zu setzen statt neu zu
      // fragen zeigt genau das an, ohne einen zweiten Weg zum Server.
      client.setQueryData(keys.settings, antwort)
    },
  })
}

export function useThreads() {
  return useQuery({
    queryKey: keys.threads,
    queryFn: () => api.get<Thread[]>('/api/threads'),
    // Bis der Live-Stream aus Etappe 2 da ist, hält ein ruhiges Nachfragen
    // die Chatliste aktuell.
    refetchInterval: 20_000,
  })
}

/**
 * Die Route einer Reise.
 *
 * Lange haltbar und ohne Takt: eine Aufzeichnung kommt schubweise, nicht im
 * Minutentakt -- und sie ist um Größenordnungen schwerer als eine Seite
 * Nachrichten. Wer sie neu will, öffnet die Karte neu.
 */
export function useTracks(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.tracks(tripId),
    queryFn: () => api.get<TracksResponse>(`/api/trips/${tripId}/tracks`),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useUploadTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (datei: File) =>
      api.upload<Track>(`/api/trips/${tripId}/tracks`, datei, undefined, {
        'x-datei-name': datei.name,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
      // **Auch die Highlights.** Der Import liest Aufenthalte aus der
      // Aufzeichnung und legt sie als Vorschläge an -- ohne diese Zeile stünde
      // in der Leiste „1 Vorschlag", und auf der Karte wäre keiner. Genau so
      // im Prüflauf aufgefallen.
      void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    },
  })
}

/**
 * Eine vorhandene Aufzeichnung noch einmal auf Aufenthalte ansehen.
 *
 * **Eine Datei je Aufruf**, und die Oberfläche geht die Liste durch. Neunzehn
 * Dateien in einer Anfrage wären auf einem geteilten Paket der Bau, der nach
 * der halben Route in die Laufzeitgrenze läuft.
 */
export function useRescan(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (trackId: number) => api.post<RescanResponse>(`/api/tracks/${trackId}/rescan`),
    onSuccess: (antwort) => {
      // Nur wenn etwas entstanden ist -- sonst holt ein Durchgang über zwanzig
      // Dateien zwanzigmal dieselbe Liste.
      if (antwort.suggested > 0) {
        void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      }
    },
  })
}

export function useDeleteTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/tracks/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
    },
  })
}

/**
 * Die Highlights einer Reise.
 *
 * Was hier ankommt, hat der Server schon gefiltert: ein Mitlesender bekommt
 * nur Freigegebenes und nur bis zu seinem Stichtag. Die Oberfläche sortiert
 * nicht nach und rechnet nichts nach — sie zeigt, was dasteht.
 */
export function useHighlights(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.highlights(tripId),
    queryFn: () => api.get<HighlightsResponse>(`/api/trips/${tripId}/highlights`),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Die Abstimmungen einer Reise (#56).
 *
 * **Was darin steht, entscheidet der Server** — je nachdem, wer fragt. Diese
 * Stelle filtert nichts nach und hält auch nichts zurück; wer eine Regel in
 * der Oberfläche zum zweiten Mal aufschreibt, hat zwei Regeln, und die zweite
 * läuft irgendwann auseinander.
 */
export function usePolls(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.polls(tripId),
    queryFn: () => api.get<PollsResponse>(`/api/trips/${tripId}/polls`),
    enabled,
    staleTime: 30_000,
  })
}

/**
 * Anlegen, abstimmen, schließen.
 *
 * **Alle drei über einen Weg**, weil alle drei dasselbe brauchen: der Server
 * antwortet mit der fertigen Abstimmung, danach werden Liste **und** Verlauf
 * neu geholt. Der Verlauf muss mit: die Abstimmung steht dort als Kärtchen an
 * ihrem Beitrag, und eine Stimme, die nur in einer der beiden Ansichten
 * ankommt, ist der Fehler, den dieses Projekt „zwei Quellen für dieselbe
 * Frage" nennt.
 */
export function usePollAktion(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (
      aktion:
        | { art: 'anlegen'; question: string; options: string[]; multi: boolean }
        | { art: 'abstimmen'; pollId: number; optionIds: number[] }
        | { art: 'schliessen'; pollId: number },
    ): Promise<Poll> => {
      if (aktion.art === 'anlegen') {
        return api.post<Poll>(`/api/trips/${tripId}/polls`, {
          question: aktion.question,
          options: aktion.options,
          multi: aktion.multi,
        })
      }
      if (aktion.art === 'abstimmen') {
        return api.post<Poll>(`/api/polls/${aktion.pollId}/vote`, {
          option_ids: aktion.optionIds,
        })
      }
      return api.post<Poll>(`/api/polls/${aktion.pollId}/close`, {})
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.polls(tripId) })
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
    },
  })
}

/**
 * The weather card (0.31.0): the device's position goes to the server, the
 * server asks Apple and posts the card to everyone. Afterwards the thread is
 * fetched anew, like after a poll -- the card is a message.
 */
/**
 * The day card (#79): the server says which day and hands over the points,
 * the browser draws the map (`zeichnen`), the server stores card and map.
 * Nothing is believed from the picture but the picture.
 */
export function useTageskarte(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (schritte: {
      /** The day the traveller chose (0.41.0) -- one of the days the server offered. */
      day: string
      /** And the part of it (0.42.0), as instants; the server checks both against the day. */
      from?: string
      to?: string
      /** The line and the published points of the day (#80), as the server hands them over. */
      zeichnen: (punkte: [number, number][], nadeln: DayCardPreview['highlights']) => Promise<string | null>
      vorher: (stand: 'zeichnen' | 'schicken') => void
    }) => {
      const fenster = { day: schritte.day, from: schritte.from, to: schritte.to }
      const vorschau = await api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, fenster)
      schritte.vorher('zeichnen')
      const map = await schritte.zeichnen(vorschau.points, vorschau.highlights ?? [])
      // Its thumbnail with it (1.3.0): the map is a photo in the chat like
      // any other and gets the same little picture for the tile.
      const thumb = map === null ? null : await vorschauDataUrl(map, (await ensureSettings(client))?.thumb_max_edge)
      schritte.vorher('schicken')
      return api.post<{ message_id: number }>(`/api/trips/${tripId}/daycard`, { ...fenster, day: vorschau.day, map: map ?? '', thumb: thumb ?? '' })
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** The thumbnail of a drawn JPEG data URL, as a data URL again -- or `null`. */
async function vorschauDataUrl(dataUrl: string, kante: number | undefined): Promise<string | null> {
  try {
    const blob = await (await fetch(dataUrl)).blob()
    const klein = await vorschauAus(blob, kante)
    if (klein === null) return null
    return await new Promise<string | null>((resolve) => {
      const leser = new FileReader()
      leser.onload = () => resolve(typeof leser.result === 'string' ? leser.result : null)
      leser.onerror = () => resolve(null)
      leser.readAsDataURL(klein)
    })
  } catch {
    return null
  }
}

export function useWetterkarte(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (where: { lat: number; lon: number }) =>
      api.post<{ message_id: number }>(`/api/trips/${tripId}/weather`, where),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Anlegen, ändern, wegnehmen, reagieren, freigeben.
 *
 * Alle über denselben Weg: der Server antwortet mit dem fertigen Highlight,
 * und danach wird die Liste neu geholt. Ein Punkt auf der Karte ist nichts,
 * was sich lohnt, im Zwischenspeicher von Hand nachzupflegen — dafür hängt zu
 * viel daran (Freigabe, Chatbeitrag, Reihenfolge).
 */
export function useHighlightMutations(tripId: number) {
  const client = useQueryClient()
  const frisch = () => {
    void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    // Eine Freigabe schreibt einen Chatbeitrag -- der gehört sofort in den
    // Verlauf, sonst steht er erst nach dem nächsten Abholen da.
    void client.invalidateQueries({ queryKey: keys.messages(tripId) })
    void client.invalidateQueries({ queryKey: keys.threads })
  }

  const anlegen = useMutation({
    mutationFn: (input: HighlightEingabe) =>
      api.post<Highlight>(`/api/trips/${tripId}/highlights`, input),
    onSuccess: frisch,
  })

  const aendern = useMutation({
    mutationFn: (input: HighlightEingabe & { id: number }) => {
      const { id, ...rest } = input
      return api.patch<Highlight>(`/api/highlights/${id}`, rest)
    },
    onSuccess: frisch,
  })

  const wegnehmen = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/highlights/${id}`),
    onSuccess: frisch,
  })

  const reagieren = useMutation({
    mutationFn: (input: { id: number; emoji: string }) =>
      api.post<Highlight>(`/api/highlights/${input.id}/reactions`, { emoji: input.emoji }),
    onSuccess: frisch,
  })

  const freigeben = useMutation({
    mutationFn: (id: number) => api.post<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const zurueckziehen = useMutation({
    mutationFn: (id: number) => api.delete<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const alleFreigeben = useMutation({
    mutationFn: (until: string) =>
      api.post<PublishManyResponse>(`/api/trips/${tripId}/highlights/publish`, { until }),
    onSuccess: frisch,
  })

  return { anlegen, aendern, wegnehmen, reagieren, freigeben, zurueckziehen, alleFreigeben }
}

/** Was beim Anlegen und Ändern mitgeht -- alles freiwillig bis auf den Titel. */
export interface HighlightEingabe {
  lat?: number
  lon?: number
  title?: string
  body?: string
  category?: string
  happened_at?: string
  position_source?: Highlight['position_source']
  /** Die Schublade aus der Umgebungssuche — für das Zeichen vor dem Titel. */
  poi_category?: string
  time_source?: NonNullable<Highlight['time_source']>
  /** Die Uhr an diesem Punkt. Leer mitgeschickt heißt „gilt nicht mehr". */
  tz_offset_min?: number | ''
  show_in_chat?: boolean
  media_ids?: number[]
  /**
   * Was die Vorschau geholt hat — Ortsname, der rohe Block der Ortssuche, das
   * Wetter zur Stunde.
   *
   * Nur mitschicken, wenn wirklich frisch geholt: sie **überschreiben**, was am
   * Highlight steht, und der Server stempelt `enriched_at` dazu. Der Server
   * prüft die Form beim Annehmen; erfundene Felder fallen weg.
   */
  place_name?: string
  place?: Record<string, unknown> | null
  weather?: Record<string, unknown> | null
}

/**
 * Ort und Wetter nachholen — eines nach dem anderen.
 *
 * **Warum die Oberfläche das treibt und nicht der Server beim Import:** ein
 * Import mit fünfundzwanzig Aufenthalten wären fünfzig Abrufe nach außen in
 * einer einzigen Anfrage. Auf einem geteilten Paket mit begrenzter Laufzeit
 * ist das der Bau, der nach einem langen Reisetag abbricht und die halbe
 * Route eingespielt zurücklässt.
 *
 * Also hier, nacheinander, während die Kartenseite offen ist. Jedes Highlight
 * wird **einmal** geholt; der Server merkt sich das (`enriched_at`) auch dann,
 * wenn nichts zurückkam.
 */
export function useAngaben(tripId: number, highlights: Highlight[], erlaubt: boolean) {
  const client = useQueryClient()
  const laeuft = useRef(false)
  const [offen, setOffen] = useState(0)

  const fehlend = highlights.filter((h) => h.enriched_at === null).length

  useEffect(() => {
    if (!erlaubt || laeuft.current) return
    const ids = highlights.filter((h) => h.enriched_at === null).map((h) => h.id)
    if (ids.length === 0) return

    laeuft.current = true
    setOffen(ids.length)
    let abgebrochen = false

    void (async () => {
      for (const id of ids) {
        if (abgebrochen) break
        try {
          await api.post(`/api/highlights/${id}/enrich`)
        } catch (fehler) {
          // Nicht eingerichtet oder kein Netz: dann bringt auch der nächste
          // nichts. Ein einzelner Fehlschlag am Dienst hält den Lauf dagegen
          // nicht auf — der Server merkt sich ihn und geht weiter.
          if (fehler instanceof ApiError && (fehler.offline || fehler.status === 409)) break
        }
        setOffen((n) => Math.max(0, n - 1))
      }

      laeuft.current = false
      setOffen(0)
      // Erst am Ende neu laden: mittendrin käme eine neue Liste herein und
      // der Lauf finge von vorn an.
      if (!abgebrochen) void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    })()

    return () => {
      abgebrochen = true
    }
    // An der **Zahl** der offenen Einträge, nicht an der Liste: die ist bei
    // jedem Abruf ein neues Feld, und der Effekt liefe bei jedem Rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, erlaubt, fehlend])

  return offen
}

/**
 * Die zwei Vorschläge: Ort → Zeit und Zeit → Ort.
 *
 * Beide sind Vorschläge und nichts weiter. Die Route ist beim Import
 * ausgedünnt worden, und zwischen zwei behaltenen Punkten liegt eine gerade
 * Linie, auf der niemand war — deshalb kommt der Abstand mit heraus und die
 * Oberfläche lässt ablehnen.
 */
/**
 * Ort und Wetter zu einem Punkt ansehen, bevor etwas gespeichert ist.
 *
 * Der Weg über den Server ist der Punkt: nach außen geht so seine Adresse und
 * nicht die des Geräts. Gespeichert wird dabei nichts — was das Formular zeigt,
 * ist eine Auskunft, kein Eintrag. Erst das gesicherte Highlight holt sie sich
 * über `enrich` noch einmal und behält sie.
 */
export function useLookup(tripId: number) {
  return useMutation({
    mutationFn: (input: { lat: number; lon: number; at?: string }) =>
      api.post<LookupResponse>(`/api/trips/${tripId}/lookup`, input),
  })
}

/**
 * Was rund um den Punkt liegt — Lokale, Museen, Strände.
 *
 * **Geschickt wird eine Marke, kein Suchwort.** Aus `essen` macht der Server
 * „Restaurant" und eine Handvoll Apple-Kategorien (`server/src/Umgebung.php`);
 * hier steht davon nichts. Das ist nicht nur Ordnung: ein frei eingetipptes
 * Wort wäre die Suchanfrage eines Menschen bei einem fremden Dienst, und das
 * ist etwas anderes als eine von sieben Schubladen.
 *
 * Und wie bei `useLookup` geht die Anfrage über den eigenen Server — nach außen
 * geht seine Adresse, nicht die des Geräts.
 */
export function useUmgebung(tripId: number) {
  return useMutation({
    // `weit` wählt den zweiten, größeren Umkreis aus den Einstellungen --
    // für die Orte, die an einem Punkt als Zeile mitlaufen.
    // `kind` ist eine der acht Schubladen, `q` ein getipptes Wort — genau
    // eines von beidem. Mit Wort sucht der Server ohne Kategorien: Apple führt
    // keine für Pizzeria oder Imbiss, und ein Filter könnte den Treffer nur
    // wieder wegnehmen.
    mutationFn: (input: {
      kind?: string
      q?: string
      lat: number
      lon: number
      weit?: boolean
    }) => api.post<UmgebungResponse>(`/api/trips/${tripId}/umgebung`, input),
  })
}

export function useVorschlag(tripId: number) {
  const aufDieRoute = useMutation({
    mutationFn: (input: { lat: number; lon: number }) =>
      api.post<SnapResponse>(`/api/trips/${tripId}/snap`, input),
  })
  const zurZeit = useMutation({
    mutationFn: (at: string) => api.post<LocateResponse>(`/api/trips/${tripId}/locate`, { at }),
  })
  return { aufDieRoute, zurZeit }
}

/**
 * Die Reise — und zwar wirklich, nicht „vielleicht schon da".
 *
 * **Dieselbe Falle wie bei `ensureSettings()`, und sie ist zugeschnappt.**
 * Wer ein Foto auswählt, während die Abfrage noch unterwegs ist, gilt für
 * einen Augenblick als Mitlesender — und seit #59 hängt daran, mit welcher
 * Kantenlänge verkleinert wird. Der Prüfstand hat es gemessen: das Foto des
 * Reisenden kam mit 800 statt 2000 Punkten an, und es sieht dabei aus wie
 * eines, das der Einstellung folgt.
 *
 * Läuft die Abfrage schon oder ist sie frisch, kostet das nichts.
 */
export async function ensureTrip(client: QueryClient, tripId: number): Promise<Trip | null> {
  if (tripId <= 0) return null
  try {
    return await client.ensureQueryData({
      queryKey: keys.trip(tripId),
      queryFn: () => api.get<Trip>(`/api/trips/${tripId}`),
    })
  } catch {
    return null
  }
}

export function useTrip(tripId: number) {
  return useQuery({
    queryKey: keys.trip(tripId),
    queryFn: () => api.get<Trip>(`/api/trips/${tripId}`),
    // **Keine Reise, keine Anfrage.** Seit die Hülle die offene Reise aus dem
    // Pfad ableitet (#49), wird dieser Haken auch mit 0 aufgerufen -- ohne
    // diese Zeile liefe auf jeder Seite außerhalb einer Reise eine Abfrage auf
    // `/api/trips/0` und endete in einem 404.
    enabled: tripId > 0,
  })
}

/**
 * Selbst aussteigen.
 *
 * Danach ist die Reise für dieses Konto weg — die Liste und alles, was an
 * ihr hängt, wird deshalb neu geholt und der Aufrufer schickt auf die
 * Startseite.
 */
export function useLeaveTrip(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/trips/${tripId}/members/me`),
    onSuccess: () => {
      // **Den gemerkten Ort mit vergessen** -- sonst schickt `StartPane`
      // einen beim nächsten Blick auf die Übersicht geradewegs in die Reise
      // zurück, aus der man gerade ausgestiegen ist. Dort steht dann „Diese
      // Reise gibt es nicht": genau die Sackgasse, gegen die es `vergissOrt`
      // gibt. Im Prüflauf aufgefallen, nicht am Schreibtisch.
      vergissOrt()
      void client.invalidateQueries()
    },
  })
}

/**
 * Den eigenen Namen ändern (#40).
 *
 * Danach steht er über allem, was dieses Konto geschrieben hat — deshalb wird
 * nicht nur `me` erneuert, sondern alles: Verläufe, Chatliste, Empfänger.
 * Ein Name, der an einer Stelle noch der alte ist, sieht aus wie ein zweiter
 * Mensch.
 */
export function useNameAendern() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (display_name: string) =>
      api.post<Me>('/api/auth/name', { display_name }),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void client.invalidateQueries()
    },
  })
}

/**
 * Das eigene Passwort ändern (#45).
 *
 * **Der Zwischenspeicher bleibt, wie er ist.** Es ändert sich nichts an dem,
 * was dieses Konto sehen darf — nur die anderen Geräte sind danach abgemeldet,
 * und die erfahren es an ihrer nächsten Anfrage.
 */
export function usePasswortAendern() {
  return useMutation({
    mutationFn: (was: { current_password: string, new_password: string }) =>
      api.post<Me>('/api/auth/password', was),
  })
}

export function useMembers(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.members(tripId),
    queryFn: () => api.get<Member[]>(`/api/trips/${tripId}/members`),
    enabled,
  })
}

export function useInvitations(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitations(tripId),
    queryFn: () => api.get<Invitation[]>(`/api/trips/${tripId}/invitations`),
    enabled,
  })
}

export function useInvitationPreview(code: string) {
  return useQuery({
    queryKey: ['invitation', code],
    queryFn: () => api.get<InvitationPreview>(`/api/invitations/${code}`),
    retry: false,
  })
}

/**
 * Wie viele Seiten eine Zusammenführung höchstens nachholt, um die Lücke zu
 * schließen.
 *
 * Fünf Seiten sind zweihundert Nachrichten seit dem letzten Takt -- wer so
 * lange weg war, hat keinen Chat offen gehabt, sondern die Anwendung neu
 * aufgebaut. Reicht es nicht, gilt der geladene Ausschnitt als verloren und
 * die Anzeige beginnt wieder oben; das ist der heutige Zustand und damit
 * kein Rückschritt. Ohne Grenze wäre es eine Schleife über den ganzen
 * Verlauf.
 */
const LUECKE_MAX_SEITEN = 5

/**
 * Die neueste Seite in den geladenen Ausschnitt einmischen (#42).
 *
 * **Was einmal geladen ist, bleibt geladen.** Der Ausschnitt reicht von der
 * neuesten Nachricht bis zur ältesten geladenen und wächst nur; ein Neuabruf
 * ist eine Zusammenführung und kein Ersatz.
 *
 * Innerhalb des Bereichs, den die frische Antwort abdeckt, **gilt sie**: was
 * dort fehlt, ist gelöscht und fliegt raus, was sich geändert hat (Haken,
 * Reaktionen, Bearbeitungen), kommt in der neuen Fassung. Darunter bleibt der
 * alte Stand unangetastet -- das ist der Teil, den der Knopf „Ältere
 * Nachrichten" geholt hat.
 *
 * `next_before` gehört der **ältesten geladenen** Nachricht, nicht der letzten
 * Antwort. Überlebt unten etwas, gilt der alte Merker weiter; sonst der der
 * letzten geholten Seite.
 *
 * `last_read_id` dagegen kommt aus der frischen Antwort -- wie bisher. Die
 * Trennlinie hängt ohnehin an dem Wert, den `ThreadPage` beim Aufbau
 * festhält; ein späterer Takt verschiebt sie nicht.
 */
export function seitenZusammenfuehren(
  alt: MessagePage | undefined,
  frisch: MessagePage,
): MessagePage {
  if (alt === undefined || alt.messages.length === 0) return frisch
  // Leere frische Antwort: dann deckt sie nichts ab, und alles Alte bleibt.
  const letzteFrische = frisch.messages.at(-1)
  const frischAelteste = letzteFrische === undefined
    ? Number.POSITIVE_INFINITY
    : letzteFrische.id
  const unten = alt.messages.filter((m) => m.id < frischAelteste)
  return {
    messages: [...frisch.messages, ...unten],
    next_before: unten.length > 0 ? alt.next_before : frisch.next_before,
    last_read_id: frisch.last_read_id,
  }
}

/**
 * Berühren sich die beiden Ausschnitte?
 *
 * **Sonst risse die Zusammenführung ein Loch in die Zeitachse.** Sind seit dem
 * letzten Takt mehr Nachrichten dazugekommen, als eine Seite fasst, liegt die
 * älteste der frischen Antwort noch über der neuesten geladenen -- dazwischen
 * fehlt dann alles. Gemessen wird genau das und nicht die Anzahl: eine Seite
 * ist vierzig Nachrichten, aber der Server entscheidet das, nicht der Client.
 */
function beruehrenSich(alt: MessagePage, frisch: MessagePage): boolean {
  const frischAelteste = frisch.messages.at(-1)
  const altNeueste = alt.messages.at(0)
  if (frischAelteste === undefined || altNeueste === undefined) return true
  return frischAelteste.id <= altNeueste.id
}

/**
 * Der Verlauf eines Chats.
 *
 * Geladen wird seitenweise von neu nach alt; die Anzeige dreht das um. Statt
 * `useInfiniteQuery` wird hier bewusst eine flache Liste im Cache gehalten:
 * neue Nachrichten kommen vorne dazu, ältere hinten -- das ist mit einer
 * einzigen Liste einfacher zu denken als mit Seiten, die sich überlappen.
 *
 * **Der Takt fragt nur am neuen Ende, und seine Antwort ersetzt nichts**
 * (#42). Aus dem Betrieb: „wenn man diese mit dem Button ‚ältere Nachrichten'
 * abruft, werden diese angezeigt, aber nach einigen Sekunden verschwinden sie
 * wieder." Genau so war es gebaut: der Abruf holt die neueste Seite, und sein
 * Ergebnis wurde als **ganzer** Cache-Eintrag geschrieben -- alles, was der
 * Knopf angehängt hatte, war damit weg. Drei Auslöser, nicht einer: der Takt
 * hier, `refetchOnWindowFocus` aus `main.tsx` und jeder Neuaufbau.
 *
 * Deshalb liest die Abfrage ihren eigenen Stand und mischt die Antwort ein
 * (`seitenZusammenfuehren`). Das ist dieselbe Bauweise wie in
 * `useOlderMessages`, nur in die andere Richtung -- und dieselbe Regel wie
 * bei den Sendewegen weiter unten, die die Liste seit jeher verändern, statt
 * sie zu ersetzen.
 */
export function useMessages(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useQuery({
    queryKey: keys.messages(tripId, withUser),
    queryFn: async () => {
      const hole = (before?: number) =>
        api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
          with: withUser,
          before,
        })

      const frisch = await hole()
      const alt = client.getQueryData<MessagePage>(keys.messages(tripId, withUser))
      if (alt === undefined || alt.messages.length === 0) return frisch

      // **Die Lücke schließen, bevor eingemischt wird.** Solange sich die
      // beiden Ausschnitte nicht berühren, wird weiter nach unten geholt --
      // höchstens `LUECKE_MAX_SEITEN` mal. Danach steht der frische
      // Ausschnitt für sich, statt einen Verlauf mit einem Loch zu zeigen.
      let zusammen = frisch
      for (let i = 0; i < LUECKE_MAX_SEITEN && !beruehrenSich(alt, zusammen); i++) {
        const cursor = zusammen.next_before
        if (cursor === null) break
        const weiter = await hole(cursor)
        zusammen = {
          messages: [...zusammen.messages, ...weiter.messages],
          next_before: weiter.next_before,
          last_read_id: zusammen.last_read_id,
        }
      }
      if (!beruehrenSich(alt, zusammen)) return zusammen

      return seitenZusammenfuehren(alt, zusammen)
    },
    refetchInterval: 15_000,
  })
}

export function useOlderMessages(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (before: number) =>
      api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
        with: withUser,
        before,
      }),
    onSuccess: (page) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          // **Der Lesestand bleibt der von vorhin.** Er gehört dem Öffnen,
          // nicht dem Nachladen -- sonst verschöbe sich die Trennlinie beim
          // Blättern nach hinten, obwohl niemand etwas gelesen hat.
          ? {
              messages: [...old.messages, ...page.messages],
              next_before: page.next_before,
              last_read_id: old.last_read_id,
            }
          : page,
      )
    },
  })
}

/**
 * Lädt ein verkleinertes Foto hoch, noch bevor der Beitrag abgeschickt ist.
 *
 * Aufnahmeort und -zeit gehen als Kopfzeilen mit — der Server legt sie an der
 * Datei ab, und die Karte baut daraus später einen Vorschlag, ohne das Bild
 * noch einmal zu lesen.
 */
export function useUploadMedia(tripId: number) {
  return useMutation({
    mutationFn: async (input: {
      blob: Blob
      aufnahme?: Aufnahme
      /** Das Vorschaubild aus `prepareImage()` (#57) — freiwillig. */
      thumb?: Blob | null
      onProgress?: (anteil: number) => void
    }): Promise<Media> => {
      const medium = await api.upload<Media>(
        `/api/trips/${tripId}/media`,
        input.blob,
        input.onProgress,
        aufnahmeKopf(input.aufnahme),
      )

      /**
       * **Das Vorschaubild geht hinterher, und sein Fehlschlag ist keiner.**
       *
       * Es ist eine Beschleunigung, kein Inhalt: kommt es nicht durch, steht
       * in der Kachel das Original — langsamer, aber vollständig. Ein Foto,
       * das im Ausgangskorb hängen bleibt, weil sein Bildchen nicht ankam,
       * wäre der schlechtere Handel.
       *
       * **Und hier, nicht an jeder Aufrufstelle.** Das Standbild eines Videos
       * hat genau diese Lehre schon einmal gekostet: es stand an zwei Stellen,
       * die auseinanderliefen, und wurde deshalb in `uploadVideo()`
       * zusammengezogen. Was es nur einmal gibt, kann nicht auseinanderlaufen.
       */
      return (await vorschauHochladen(medium.id, input.thumb)) ?? medium
    },
  })
}

/**
 * Die eigenen Bilder dieser Reise — für die Auswahl „Aus dieser Reise".
 *
 * Nur wenn die Auswahl offen ist: eine Reise kann Hunderte Bilder haben, und
 * niemand braucht die Liste, solange er nur ein Highlight ansieht.
 */
export function useReisefotos(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.reisefotos(tripId),
    queryFn: () => api.get<Reisefotos>(`/api/trips/${tripId}/media`),
    enabled,
    staleTime: 30_000,
  })
}

/** Wirft ein hochgeladenes Foto weg, das doch nicht mitsoll. */
export function useDiscardMedia() {
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/media/${id}`),
  })
}

export function useSendMessage(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      mediaIds?: number[]
      clientKey?: string
      /** Worauf sich das bezieht -- der Server prüft es gegen die Regel. */
      replyToId?: number
      replyToMediaId?: number
    }) =>
      api.post<Message>(
        `/api/trips/${tripId}/messages`,
        // **Der Rumpf wird an einer Stelle gebaut** (`api/nachrichtRumpf.ts`).
        // Der Ausgangskorb baut denselben; standen sie an zwei Stellen, bekäme
        // eine von beiden das nächste Feld nicht mit.
        nachrichtRumpf(input),
        // Im Antwortverlauf schreibt der Reisende an genau diesen einen
        // Menschen. Dass daraus eine private Nachricht wird, entscheidet der
        // Server -- hier steht nur, welcher Verlauf offen ist.
        { with: withUser },
      ),
    onSuccess: (message) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? { ...old, messages: [message, ...old.messages] }
          // Ohne vorhandene Seite gibt es auch keinen Lesestand von vorhin:
          // die eigene, gerade abgeschickte Nachricht ist nichts Ungelesenes.
          : { messages: [message], next_before: null, last_read_id: message.id },
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useEditMessage(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; body: string }) =>
      api.patch<Message>(`/api/messages/${input.id}`, { body: input.body }),
    onSuccess: (message) => {
      // Die geänderte Nachricht an ihrem Platz ersetzen, statt den ganzen
      // Verlauf neu zu laden -- sonst springt die Ansicht beim Absenden.
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                // Der Haken bleibt, wie er war. Wer den Text ändert, ändert
                // nicht, wer ihn schon gesehen hat -- und die Antwort auf ein
                // PATCH weiß nichts vom geöffneten Verlauf, könnte ihn also
                // nur raten. Lieber stehenlassen als falsch neu setzen.
                m.id === message.id ? { ...message, read: m.read } : m,
              ),
            }
          : old,
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Setzt, ersetzt oder nimmt eine Reaktion zurück.
 *
 * Dasselbe Emoji noch einmal nimmt es zurück, ein anderes ersetzt es -- eine
 * Person hat je Ziel genau eine Reaktion. Der Server schickt den neuen Stand
 * der ganzen Nachricht zurück; hier wird nur diese eine ersetzt, damit der
 * Verlauf nicht springt.
 */
export function useToggleReaction(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { messageId: number; emoji: string; mediaId?: number }) =>
      api.post<{ message_id: number; reactions: Reaction[] }>(
        `/api/messages/${input.messageId}/reactions`,
        { emoji: input.emoji, media_id: input.mediaId ?? 0 },
      ),
    onSuccess: ({ message_id, reactions }) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                m.id === message_id ? { ...m, reactions } : m,
              ),
            }
          : old,
      )
    },
  })
}

/**
 * Mehrere Nachrichten auf einmal loeschen (#30).
 *
 * **Kein Sammelendpunkt, sondern N Aufrufe der bestehenden Route.** Wem eine
 * Nachricht gehoert, entscheidet der Server in genau einer Zeile
 * (`WHERE id = :id AND author_id = :user`); eine zweite Route muesste dieselbe
 * Regel ein zweites Mal hinschreiben, und das ist hier schon dreimal
 * auseinandergelaufen. Zehn Aufrufe kosten auf einem Freizeit-Hosting nichts,
 * eine doppelte Regel kostet irgendwann alles.
 *
 * **Der Reihe nach und nicht gleichzeitig:** ein Paket mit knappen Grenzen
 * dankt es, und die Reihenfolge macht die Zahl am Ende ehrlich.
 *
 * **Was schiefgeht, wird gezaehlt und gesagt.** Der Aufrufer bekommt beide
 * Zahlen und kann „3 von 5 geloescht" hinstellen, statt so zu tun, als sei
 * alles weg. Was nicht durchkam, bleibt markiert.
 *
 * **Und jede geloeschte wird an ihren Platz zurueckgelegt, nicht nur
 * aufgefrischt.**
 *
 * Aus dem Betrieb zu #30: „funktioniert meistens, aber nicht immer", geloescht
 * wurde im Privatchat eines Lesers. Ein Neuabruf **ersetzt** den Verlauf nicht,
 * er fuehrt zusammen (`seitenZusammenfuehren`, aus #42): innerhalb der frischen
 * Seite gilt die frische Antwort, darunter bleibt der geladene Ausschnitt
 * stehen -- der Teil, den „Aeltere Nachrichten" geholt hat. Wer weiter zurueck
 * loescht, bekam die alte Fassung aus dem eigenen Zwischenspeicher wieder
 * untergemischt: geloescht war sie, zu sehen blieb sie trotzdem, und der Takt
 * alle fuenfzehn Sekunden wiederholte das.
 *
 * `setQueryData` trifft die Nachricht an ihrer Stelle, egal wie weit unten die
 * liegt. **Dieselbe Machart wie beim Aendern und beim Reagieren** -- und dort
 * war es die ganze Zeit richtig; Loeschen war die eine Stelle, die nur
 * aufgefrischt hat.
 *
 * Was eine geloeschte Nachricht ist, sagt dabei der **Server**: er gibt sie
 * zurueck, und was hier ankommt, wird hingelegt. Die Form im Browser
 * nachzubauen waere dieselbe Regel an zwei Stellen.
 *
 * Aufgefrischt wird **einmal** am Ende statt nach jedem Loeschen -- sonst
 * zeichnet der Verlauf bei zehn Nachrichten zehnmal neu.
 */
export function useDeleteMessages(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (
      ids: number[],
    ): Promise<{ weg: Message[]; geblieben: number[] }> => {
      const weg: Message[] = []
      const geblieben: number[] = []
      for (const id of ids) {
        try {
          weg.push(await api.delete<Message>(`/api/messages/${id}`))
        } catch {
          geblieben.push(id)
        }
      }
      return { weg, geblieben }
    },
    onSuccess: ({ weg }) => {
      if (weg.length > 0) {
        const geloescht = new Map(weg.map((m) => [m.id, m]))
        client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
          old
            ? { ...old, messages: old.messages.map((m) => geloescht.get(m.id) ?? m) }
            : old,
        )
      }
      // Die Vorschau in der Chatliste haengt am Verlauf und nicht an dieser
      // einen Nachricht -- die holt sich der Server.
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Was ein Zurücksetzen der Reise kosten würde (#12).
 *
 * **Erst auf Verlangen**, nicht beim Öffnen der Reiseseite: es sind vier
 * Zählungen über den ganzen Inhalt, und die meisten Besuche der Seite haben
 * damit nichts zu tun.
 *
 * `staleTime: 0`: zwischen Ansehen und Bestätigen kann etwas dazugekommen
 * sein, und die Zahlen sollen die von jetzt sein.
 */
export function useResetVorschau(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['reset-vorschau', tripId] as const,
    queryFn: () => api.get<TripResetVorschau>(`/api/trips/${tripId}/reset`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Die Reise auf Anfang stellen (#12) — der Inhalt geht, die Reise bleibt.
 *
 * **Danach ist alles ungültig, was aus dieser Reise stammt.** Deshalb hier
 * ausnahmsweise die breite Auffrischung: Chatliste, Route, Punkte, Fotos, die
 * Reise selbst und der Speicherstand. Einzeln nachzuhalten, was sich geändert
 * hat, wäre dieselbe Liste ein zweites Mal — und die eine, die dabei vergessen
 * wird, zeigt hinterher Nachrichten an, die es nicht mehr gibt.
 *
 * **Der Verlauf wird weggeworfen und nicht aufgefrischt**, und das ist der
 * Unterschied, an dem es hing.
 *
 * Aus dem Betrieb, beim ersten Versuch am Gerät: „die Bilder / Videos sind
 * weg, die Nachrichten nicht." Auf dem Server waren sie sehr wohl weg — der
 * Browser hielt sie fest. `useMessages` **ersetzt** den Verlauf nicht, es
 * führt zusammen (`seitenZusammenfuehren`, aus #42), und für eine leere
 * frische Antwort steht dort ausdrücklich: „dann deckt sie nichts ab, und
 * alles Alte bleibt." Genau der Fall tritt nach dem Zurücksetzen ein. Eine
 * Auffrischung holt sich also die Leere und mischt die alten Nachrichten
 * wieder darunter.
 *
 * **Und Neuladen half nicht.** Der Verlauf steht in der Positivliste des
 * Vorrats (`vorrat.ts`) und wird vor dem ersten Zeichnen wieder eingespielt —
 * er überlebt damit auch das Schließen der App. Was nicht aus dem
 * Zwischenspeicher genommen wird, kommt sieben Tage lang zurück.
 *
 * `removeQueries` nimmt dem Neuabruf das Alte weg, an dem er sonst festhält.
 * **Dieselbe Zusammenführung wie bei #30, nur von der anderen Seite** — dort
 * überlebte eine einzelne gelöschte Nachricht, hier überleben alle.
 */
export function useDeleteVorschau(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['delete-vorschau', tripId] as const,
    queryFn: () => api.get<TripDeleteVorschau>(`/api/trips/${tripId}/delete`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Delete the trip for good (#15).
 *
 * Afterwards nothing of it may stay in memory or on disk: the same trap as
 * after a reset (see `useTripReset`), plus the trip itself in the lists. The
 * caller leaves the page -- what confirms the deletion must not live inside
 * what the deletion removes (§6dc).
 */
export function useTripDelete(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<TripDeleteBefund>(`/api/trips/${tripId}/delete`, { code }),
    onSuccess: () => {
      client.removeQueries({ queryKey: ['messages', tripId] })
      client.removeQueries({ queryKey: keys.trip(tripId) })
      void vorratLeeren()
      for (const key of [keys.threads, keys.trips, keys.speicher, keys.people]) {
        void client.invalidateQueries({ queryKey: key })
      }
    },
  })
}

export function useTripReset(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<TripResetBefund>(`/api/trips/${tripId}/reset`, { code }),
    onSuccess: () => {
      client.removeQueries({ queryKey: ['messages', tripId] })
      /*
       * **Und der Vorrat auf der Platte gleich mit.**
       *
       * `removeQueries` räumt den Arbeitsspeicher; auf die Platte kommt der
       * neue Stand erst mit dem nächsten gedrosselten Schreibvorgang
       * (`vorrat.ts`, eine Sekunde). Wer in dieser Lücke neu lädt, bekommt den
       * alten Satz **vor dem ersten Zeichnen** wieder eingespielt -- genau
       * das, was hier gerade weggeworfen wurde.
       *
       * **Das ist die Lücke, die #12 offen gelassen hat.** Der Prüflauf war
       * daran mal grün und mal rot, je nachdem, welche der beiden Uhren
       * gewann; gefunden wurde es erst, als er den Vorrat selbst mitgemessen
       * hat statt nur die Sprechblasen. Er zählt jetzt beides, und die Zahlen
       * stimmen überein.
       *
       * Der ganze Vorrat und nicht nur diese Reise: er liegt als **ein** Satz
       * auf der Platte. Was fehlt, holt der nächste Abruf.
       */
      void vorratLeeren()
      for (const key of [
        keys.threads,
        keys.trips,
        keys.trip(tripId),
        keys.tracks(tripId),
        keys.highlights(tripId),
        keys.reisefotos(tripId),
        keys.speicher,
        ['reset-vorschau', tripId],
      ]) {
        void client.invalidateQueries({ queryKey: key })
      }
    },
  })
}

export function useMarkRead(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<void>(`/api/trips/${tripId}/read`, {}, { with: withUser }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Die Kontenliste -- der Server gibt sie nur der Verwaltung. */
export function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: keys.people,
    queryFn: () => api.get<Person[]>('/api/people'),
    enabled,
  })
}

/** Entfernt jemanden aus einer Reise -- nur der Absender darf das. */
/**
 * Wer sich dieser Reise zuordnen lässt (#36) -- nur für den Absender.
 *
 * `staleTime: 0`: die Liste ändert sich mit jedem Häkchen, und ein Stand von
 * vorhin wäre genau die Zeile, die man gerade angetippt hat.
 */
export function useCandidates(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.candidates(tripId),
    queryFn: () => api.get<Candidate[]>(`/api/trips/${tripId}/candidates`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Ordnet jemanden ohne Einladungslink zu (#36).
 *
 * Wirkt sofort -- kein Sammeln, kein Speichern-Knopf. Ein Häkchen, das erst
 * später wirkt, ist die stille Absage, die dieses Projekt nicht will.
 */
export function useAddMember(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<void>(`/api/trips/${tripId}/members/${userId}`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.candidates(tripId) })
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.requests(tripId) })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Verschickt die Willkommensnachricht (#36).
 *
 * Ohne `userId` an alle Empfänger, die sie noch nicht haben; mit `userId` an
 * genau den einen. Zurück kommt, wie viele es waren -- „es ist etwas
 * passiert" wäre hier zu wenig, gerade wenn es null ist.
 */
export function useSendWelcome(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId?: number) =>
      api.post<{ sent: number }>(
        `/api/trips/${tripId}/welcome`,
        userId === undefined ? {} : { user: userId },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
      void client.invalidateQueries({ queryKey: keys.messages(tripId) })
    },
  })
}

export function useRemoveMember(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.delete<void>(`/api/trips/${tripId}/members/${userId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Legt ein Konto still oder gibt es wieder frei -- nur die Verwaltung. */
/**
 * Löscht ein Konto endgültig -- nur für die, an denen nichts hängt.
 *
 * Der Server prüft das noch einmal selbst; hier wird der Knopf gar nicht
 * erst angeboten, wo er scheitern würde.
 */
/**
 * Was ein Löschen kosten würde -- gezählt vom Server (#47).
 *
 * `staleTime: 0`: die Zahlen stehen in einer Rückfrage, die etwas Endgültiges
 * ankündigt. Eine Sekunde alt ist hier zu alt.
 */
export function useLoeschvorschau(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ['loeschvorschau', id],
    queryFn: () => api.get<Loeschvorschau>(`/api/people/${id}/loeschvorschau`),
    enabled,
    staleTime: 0,
  })
}

export function useDeletePerson() {
  const client = useQueryClient()
  return useMutation({
    // Die abgetippte E-Mail geht im Rumpf mit -- als Suchparameter stünde eine
    // Adresse in jedem Server- und Proxy-Protokoll.
    mutationFn: (input: { id: number; email: string }) =>
      api.delete<Loeschbefund>(`/api/people/${input.id}`, { email: input.email }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

export function useSetDisabled() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; disabled: boolean }) =>
      api.post<{ id: number; disabled: boolean }>(`/api/people/${input.id}/disabled`, {
        disabled: input.disabled,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/** Gibt einem Admin das Merkmal des Supervisors oder nimmt es (#62). */
export function useSetSupervisor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; supervisor: boolean }) =>
      api.post<{ id: number; supervisor: boolean }>(`/api/people/${input.id}/supervisor`, {
        supervisor: input.supervisor,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/**
 * Die Werte der `.env` für den Supervisor (#62). `staleTime: 0`: wer die
 * Seite öffnet, will den Stand der Datei, nicht den von vorhin.
 */
export function useEnv(enabled: boolean) {
  return useQuery({
    queryKey: ['operations', 'env'],
    queryFn: () => api.get<EnvSeite>('/api/operations/env'),
    enabled,
    staleTime: 0,
  })
}

/** Das Protokoll der Verwaltungshandlungen (1.7.0), die letzten hundert, neueste zuerst. */
export function useVerwaltungsprotokoll(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'log'],
    queryFn: () => api.get<Verwaltungsprotokoll>('/api/admin/log'),
    enabled,
    staleTime: 0,
  })
}

/** Ändert einen Wert der `.env`; die Antwort ist der neue Stand der ganzen Seite. */
export function useSaveEnv() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { key: string; value: string | null }) =>
      api.patch<EnvSeite>('/api/operations/env', input),
    onSuccess: (antwort) => {
      client.setQueryData(['operations', 'env'], antwort)
      // Was die Datei ändert, ändert auch die Einstellungen: Decke, Dienste, Karte.
      void client.invalidateQueries({ queryKey: keys.settings })
    },
  })
}

export function useEnvUndo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<EnvSeite>('/api/operations/env/undo', {}),
    onSuccess: (antwort) => {
      client.setQueryData(['operations', 'env'], antwort)
      void client.invalidateQueries({ queryKey: keys.settings })
    },
  })
}

export function useSelbstpruefung(enabled: boolean) {
  return useQuery({
    queryKey: ['operations', 'check'],
    queryFn: () => api.get<Selbstpruefung>('/api/operations/check'),
    enabled,
    staleTime: 0,
  })
}

export function useSetRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; role: Person['role'] }) =>
      api.post<{ id: number; role: Person['role'] }>(`/api/people/${input.id}/role`, {
        role: input.role,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/**
 * Vergibt einen zweiten Namen — oder nimmt ihn zurück (leer heißt löschen).
 *
 * Nur die Verwaltung sieht ihn je; das entscheidet der Server an jeder
 * Abfrage. Hier wird nur gespeichert.
 */
export function useSetPersonName() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; name: string }) =>
      api.post<{ id: number; admin_display_name: string | null }>(
        `/api/people/${input.id}/name`,
        { admin_display_name: input.name },
      ),
    onSuccess: () => {
      // Der Name steht nicht nur in der Personenliste: er taucht in jeder
      // Ansicht der Verwaltung auf. Also auch die Reisen und die Chatliste
      // wegwerfen — sonst stünde dort bis zum nächsten Laden der alte.
      void client.invalidateQueries({ queryKey: keys.people })
      void invalidateAll(client)
    },
  })
}

export function useCreateTrip() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      description: string
      starts_on?: string
      ends_on?: string
    }) => api.post<Trip>('/api/trips', input),
    onSuccess: () => invalidateAll(client),
  })
}

/**
 * Der Zustand einer Reise -- drei Stufen, eine Leiter.
 *
 * `active` läuft, `closed` beendet das Schreiben für alle, `private` nimmt
 * sie zusätzlich aus den Listen der Mitlesenden. Gelöscht wird in keinem
 * Fall etwas.
 */
/**
 * Titel, Beschreibung und Reisezeitraum ändern.
 *
 * Geschickt wird nur, was sich geändert hat: der Server lässt stehen, was
 * fehlt, und löscht, was leer kommt.
 */
/**
 * Was der Reisende an der Karte seiner Reise ändert (#50): dieselben
 * Schlüssel wie in den Einstellungen, damit der Regler auf der Karte und die
 * Voreinstellung in der Verwaltung ein Vokabular sprechen. Leer heißt „wie
 * das Erscheinungsbild".
 */
export type KartenAenderung = Partial<Omit<MapStyle, 'route_width' | 'daycard_route_width'>> & {
  route_width?: number | ''
  daycard_route_width?: number | ''
}

export function useUpdateTrip(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      input: Partial<
        Pick<
          Trip,
          | 'title'
          | 'description'
          | 'starts_on'
          | 'ends_on'
          | 'mark_broadcast'
          | 'welcome_text'
        >
      > & {
        /** Leer mitgeschickt löscht die Uhr der Reise — wie beim Datum. */
        tz_offset_min?: number | ''
        /**
         * Die Farben der Sprechblasen dieser Reise (#49).
         *
         * Heißen hier wie in den Einstellungen und nicht wie im Ausgang
         * (`chat_bubbles`): ein Vokabular für dieselbe Sache. Leer heißt „wie
         * das Erscheinungsbild" und löscht die Angabe.
         */
        chat_own_color?: string
        chat_other_color?: string
      } & KartenAenderung,
    ) => api.patch<Trip>(`/api/trips/${tripId}`, input),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useSetTripState(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (state: TripState) =>
      api.post<Trip>(`/api/trips/${tripId}/state`, { state }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void invalidateAll(client)
    },
  })
}

/**
 * Die aktive Reise setzen oder wieder lösen.
 *
 * **Der Server sorgt für „genau eine"** — hier wird nur gefragt. Die Liste
 * aller Reisen und die Chatliste müssen danach beide neu geholt werden: die
 * Marke wandert, und die andere Reise weiß nichts davon.
 */
export function useSetAktiv(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (aktiv: boolean) =>
      aktiv
        ? api.post<Trip>(`/api/trips/${tripId}/active`, {})
        : api.delete<Trip>(`/api/trips/${tripId}/active`),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void invalidateAll(client)
    },
  })
}

/**
 * Die Schriftgröße am Konto sichern.
 *
 * Angewendet wird sie sofort und ohne auf die Antwort zu warten — sie steht
 * schon am Wurzelelement, bevor der Server antwortet. Was hier passiert, ist
 * das Merken für die anderen Geräte.
 */
export function useSetSchrift() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (font_scale: string) => api.patch<Me>('/api/auth/me', { font_scale }),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Alles andere am Konto, was nur mit dem Aussehen zu tun hat (#54).
 *
 * Derselbe Weg wie die Schriftgröße (`PATCH /api/auth/me`) und derselbe Grund:
 * es sagt etwas über die Augen des Menschen vor dem Schirm und geht niemanden
 * sonst etwas an. Feld für Feld — was fehlt, bleibt stehen.
 */
export function useSetDarstellung() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (werte: {
      list_own_color?: string
      list_other_color?: string
      list_bg_color?: string
    }) => api.patch<Me>('/api/auth/me', werte),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Die Zahl am App-Symbol ein- oder ausschalten (0.26.0).
 *
 * Derselbe Weg (`PATCH /api/auth/me`), und aus demselben Grund am Konto:
 * der Hintergrunddienst setzt die Zahl aus dem Push, und den baut der Server
 * — ein Schalter allein im Browser hätte ihn nicht aufgehalten. Mit der
 * Antwort steht das Konto neu im Vorrat, und `useSymbolzahl()` räumt oder
 * setzt sofort.
 */
export function useSetSymbolzahl() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (app_badge: boolean) => api.patch<Me>('/api/auth/me', { app_badge }),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Wie voll es ist — nur für die Verwaltung.
 *
 * **`enabled` und kein Rateversuch:** wer keine Verwaltung ist, bekommt ein
 * 403, und ein Fehler, der bei jedem Öffnen der Einstellungen im Protokoll
 * steht, ist Lärm. Gefragt wird also nur, wenn die Karte auch dasteht.
 *
 * Kein `staleTime`: die Zahl ändert sich mit jedem Upload, und wer sie
 * ansieht, will wissen, wie es *jetzt* aussieht.
 */
export function useSpeicher(enabled: boolean) {
  return useQuery({
    queryKey: keys.speicher,
    queryFn: () => api.get<Speicher>('/api/storage'),
    enabled,
  })
}

/**
 * Welche Dateien nicht überall abspielbar sind (#41).
 *
 * **Auf Knopfdruck und nicht beim Öffnen.** Die Prüfung sieht in jede Datei
 * hinein; das ist bei ein paar hundert Videos spürbar, und niemand braucht
 * es, nur weil er die Einstellungen aufschlägt. Deshalb `enabled: false` und
 * ein `refetch()` am Knopf — dieselbe Haltung wie bei der Umgebungssuche, die
 * auch erst auf Verlangen hinausgeht.
 */
export function useMedienpruefung() {
  return useQuery({
    queryKey: ['medienpruefung'],
    queryFn: () => api.get<Medienpruefung>('/api/storage/medien'),
    enabled: false,
    // Sie ist teuer und ändert sich nur, wenn jemand etwas hochlädt oder
    // ersetzt -- ein zweiter Lauf beim Zurückkommen wäre Arbeit ohne Auskunft.
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Dateien und Einträge gegeneinander (#20).
 *
 * **Auf Knopfdruck, wie die Kodierungsprüfung** — und aus demselben Grund:
 * der Abgleich geht das ganze Medienverzeichnis durch. Ein eigener Knopf und
 * nicht derselbe, denn es sind zwei verschiedene Kosten und zwei verschiedene
 * Fragen.
 */
export function useDateiabgleich() {
  return useQuery({
    queryKey: keys.abgleich,
    queryFn: () => api.get<Dateiabgleich>('/api/storage/abgleich'),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Die Einträge wegräumen, deren Datei fehlt.
 *
 * Danach werden **beide** Karten neu geholt: der Abgleich, weil die Liste
 * kürzer wird — und der Speicherplatz, weil dessen Zahl die Bytes dieser
 * Zeilen mitgezählt hat. Bliebe die alte stehen, sähe der Knopf aus, als
 * hätte er nichts getan.
 *
 * **Den Abgleich holt aber die Karte selbst nach, nicht dieser Haken.** Er
 * steht auf `enabled: false` und läuft nur auf Knopfdruck; eine solche Abfrage
 * gilt react-query als abgeschaltet, und **weder `invalidateQueries` noch
 * `refetchQueries` fassen sie an** — beide sieben sie vorher aus. Sie wäre nur
 * als veraltet vermerkt und stünde unverändert da, bis jemand den Knopf noch
 * einmal drückt: genau die stille Absage, die es nicht geben soll. Das
 * `refetch()` an der Abfrage selbst kennt diese Sperre nicht, und deshalb
 * steht es dort, wo die Abfrage steht.
 */
export function useFehlendeRaeumen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ files: number; bytes: number }>('/api/storage/abgleich/sweep', {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

/**
 * Eine einzelne herrenlose Datei wegwerfen (#20).
 *
 * **Angesprochen über ihren Namen** — eine Zeile gibt es ja nicht. Ob sie
 * wirklich herrenlos ist, entscheidet der Server im Augenblick des Aufrufs;
 * was hier hinausgeht, ist nur eine Behauptung.
 */
export function useHerrenloseLoeschen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      api.delete<{ bytes: number }>(`/api/storage/abgleich/datei/${name}`),
    onSuccess: () => {
      // Der belegte Platz ändert sich damit auch -- diese Bytes zählte die
      // Speicherkarte zwar nie, aber die Summe des Verzeichnisses schon.
      // Die Liste holt die Karte selbst nach; der Grund steht oben.
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

/**
 * Wegräumen, was an nichts hängt.
 *
 * Danach wird die Karte neu geholt — sonst stünde die alte Zahl da, und der
 * Knopf sähe aus, als hätte er nichts getan.
 */
export function useAufraeumen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ files: number; bytes: number }>('/api/storage/sweep', {}),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.speicher }),
  })
}

export function useSetMuted(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (muted: boolean) =>
      api.post<{ muted: boolean }>(`/api/trips/${tripId}/mute`, { muted }),
    onSuccess: ({ muted }) => {
      client.setQueryData<Trip>(keys.trip(tripId), (old) =>
        old ? { ...old, muted } : old,
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Zieht einen Einladungslink zurück.
 *
 * Der Code bleibt in der Datenbank stehen (`revoked_at`), verschwindet aber
 * aus der Liste und führt nirgends mehr hin. Rückgängig macht das niemand --
 * deshalb fragt die Oberfläche vorher.
 */
export function useRevokeInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.delete<void>(`/api/trips/${tripId}/invitations/${code}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useCreateInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (maxUses?: number) =>
      api.post<Invitation>(`/api/trips/${tripId}/invitations`, { max_uses: maxUses }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useAcceptInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ state: 'joined' | 'pending'; trip: Trip | null }>(
        `/api/invitations/${code}/accept`,
      ),
    // **`me` muss mit.** Dort stehen `may_use_app` und `waiting_for` — beides
    // ändert sich durch genau diesen Klick. Ohne das landete ein
    // Anklopfender auf der Wartetafel und las dort „Sobald dich jemand zu
    // einem Reisebericht einlädt, geht es hier weiter" — die Auskunft für
    // jemanden, der nichts getan hat, an den, der gerade angeklopft hat.
    onSuccess: () => Promise.all([
      invalidateAll(client),
      client.invalidateQueries({ queryKey: keys.me }),
    ]),
  })
}

/**
 * Wo man mit dieser Einladung steht.
 *
 * Gebraucht, seit zwischen dem Klick auf den Link und dem Mitlesen eine
 * Bestätigung liegen kann. Nach dem Anlegen des Kontos wüsste die
 * Einladungsseite sonst nicht, ob sie in die Reise führen oder „wartet auf
 * Bestätigung" zeigen soll.
 */
export function useInvitationStatus(code: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitationStatus(code),
    queryFn: () => api.get<InvitationStatus>(`/api/invitations/${code}/status`),
    enabled,
    retry: false,
  })
}

export function useJoinRequests(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.requests(tripId),
    queryFn: () => api.get<JoinRequest[]>(`/api/trips/${tripId}/requests`),
    enabled,
  })
}

export function useDecideRequest(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      userId: number
      approve: boolean
      disableAccount?: boolean
      /** Antwort auf die Rückfrage „der Link ist aufgebraucht" (409). */
      trotzdem?: boolean
    }) =>
      api.post<void>(`/api/trips/${tripId}/requests/${input.userId}`, {
        approve: input.approve,
        disable_account: input.disableAccount ?? false,
        trotzdem: input.trotzdem ?? false,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.requests(tripId) })
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.people })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
      // Der Zähler des Links hat sich bewegt -- und die Liste blendet
      // aufgebrauchte Links aus.
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

/**
 * Jemanden aus dem Verteiler nehmen -- oder wieder hinein.
 *
 * Der leise Bruder von `useRemoveMember`: die Reise bleibt in seiner Liste,
 * nur kommt nichts Neues mehr dazu.
 */
export function useSetCutoff(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: number; cut: boolean }) =>
      api.post<{ user_id: number; cut_off: boolean }>(
        `/api/trips/${tripId}/members/${input.userId}/cutoff`,
        { cut: input.cut },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
    },
  })
}

export function useSetApproval(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (required: boolean) =>
      api.post<Trip>(`/api/trips/${tripId}/approval`, { required }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
    },
  })
}

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Me>('/api/auth/login', input),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useRegister() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      email: string
      password: string
      display_name: string
      invite_code?: string
    }) => api.post<Me>('/api/auth/register', input),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

/**
 * Fordert einen Link zum Zurücksetzen an.
 *
 * Der Server antwortet immer gleich, auch wenn es die Adresse nicht gibt --
 * die Oberfläche darf hier also nichts unterscheiden, was er verschweigt.
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api.post<void>('/api/auth/forgot', { email }),
  })
}

export function useResetPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<Me>('/api/auth/reset', input),
    onSuccess: (me) => {
      // Das Zurücksetzen meldet gleich an; alle bisherigen Sitzungen hat der
      // Server verworfen.
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useLogout() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => {
      // Alles verwerfen: nach dem Abmelden darf nichts aus dem Cache mehr
      // durchscheinen. Dazu gehört auch der zuletzt offene Chat -- wer sich
      // als Nächstes an diesem Gerät anmeldet, hat mit ihm nichts zu tun.
      client.clear()
      vergissOrt()
      // **Und der Vorrat auf der Platte ebenso**, sonst überlebte genau das
      // den Vorgang, was hier gerade weggeworfen wird. Der Zwischenspeicher
      // der Hülle bleibt dagegen stehen: dort liegen JavaScript, CSS und eine
      // leere Seite, nichts Persönliches -- ihn wegzuwerfen machte nur den
      // nächsten Start langsam.
      void vorratLeeren()
    },
  })
}

function invalidateAll(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.threads }),
    client.invalidateQueries({ queryKey: keys.trips }),
  ])
}

// ---- Passkeys (#21) --------------------------------------------------------

export function usePasskeys() {
  return useQuery({
    queryKey: keys.passkeys,
    queryFn: () => api.get<Passkey[]>('/api/auth/passkeys'),
  })
}

/**
 * Create a passkey: fetch the options, ask the device, hand in the response.
 *
 * Three steps in one mutation, so the card knows only one state. If the
 * person cancels at the device, `createPasskey` throws -- the card reads
 * that via `isAbort()` and says so instead of showing an error.
 */
/**
 * The browser's part comes in as a promise, started by the caller **inside
 * the gesture** (`hooks/passkeyOptions.ts` says why); here only the server's
 * part follows.
 */
export function useCreatePasskey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (job: { attestation: Promise<Registration> }) => {
      const response = await job.attestation
      return api.post<Passkey>('/api/auth/passkeys', response)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.passkeys }),
  })
}

export function useRemovePasskey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/auth/passkeys/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.passkeys }),
  })
}

/**
 * Sign in with the passkey -- the same consequences as `useLogin`.
 *
 * `mediation: 'conditional'` is the suggestion in the address field
 * (autofill); without it, it is the button. Both fetch a fresh challenge:
 * the old one is valid for five minutes and exactly once.
 */
/** As above: the assertion is the caller's, the login is the server's. */
export function usePasskeyLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (job: { assertion: Promise<Login> }) => {
      const response = await job.assertion
      return api.post<Me>('/api/auth/passkeys/login', response)
    },
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

/**
 * How long the browser waits for the server to build a book. The server
 * gives itself five minutes (`BookController`); whatever takes longer is not
 * going to arrive, and a button that says "wird gebaut" for ever is a silent
 * failure -- from use (0.30.9).
 */
const BOOK_TIMEOUT_MS = 6 * 60 * 1000

/** `AbortSignal.timeout()` where it exists (iOS 16); otherwise by hand. */
function timeLimit(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms)
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

/**
 * Have the book built as a file (#14).
 *
 * The server answers with the address of the finished file; it is fetched
 * via a link with `download`, because an iPhone needs an address it can
 * save. The maps come along as JPEG from the browser.
 */
/** The media as a ZIP (#75): the server packs, the link comes back -- with the book's time limit. */
export function useMedienExport(tripId: number) {
  return useMutation({
    mutationFn: () =>
      api.post<{ url: string; filename: string; files: number; named: number; lookups: number; bytes: number }>(`/api/trips/${tripId}/media-export`, {}, undefined, undefined, timeLimit(BOOK_TIMEOUT_MS)),
  })
}

export function useBuildBook(tripId: number) {
  return useMutation({
    mutationFn: (job: {
      format: 'pdf' | 'epub'
      with?: number
      from: string
      to: string
      maps: { route?: string; points: Record<string, string> }
    }) => api.post<{ url: string; filename: string; bytes: number; seconds: number }>(`/api/trips/${tripId}/book`, job, undefined, undefined, timeLimit(BOOK_TIMEOUT_MS)),
  })
}
